import type { ApexOptions } from 'apexcharts'
import { calcularPassoRotulosEixoX, indicesRotulosEixoX, rotuloDoPonto } from './serieDiariaOpcoes'

/**
 * Realização dos atendimentos lançados num dia ou semana, em contagem. `n` são os lançados;
 * sem dado, as três contagens são `null` (lacuna, nunca zero).
 */
export type PontoRealizacao = {
  data: string
  dataFim?: string
  n: number
  realizados: number | null
  cancelados: number | null
  faltas: number | null
}

export type PercentuaisRealizacao = {
  realizados: number | null
  cancelados: number | null
  faltas: number | null
}

export type CoresBarrasEmpilhadas = {
  realizados: string
  cancelados: string
  faltas: string
  texto: string
  grade: string
  /** Fundo do cartão: contorno que separa os segmentos vizinhos. */
  superficie: string
  /** Marcador na base da coluna sem dado (≥ 3:1 contra o fundo). */
  semDado: string
}

export type EntradaOpcoesBarrasEmpilhadas = {
  pontos: PontoRealizacao[]
  nomes: { realizados: string; cancelados: string; faltas: string }
  cores: CoresBarrasEmpilhadas
  formatarPercentual: (valor: number) => string
  formatarEixoY: (valor: number) => string
  formatarRotuloDia: (data: string) => string
  formatarIntervalo?: (dataInicio: string, dataFim: string) => string
  textoSemDados: string
  altura?: number
  larguraGrafico?: number
}

const ALTURA_PADRAO = 280
// Folga à direita para o último rótulo do eixo X não ser cortado pela borda do SVG.
const FOLGA_DIREITA = 16

/** Percentuais do ponto sobre os lançados; ponto sem dado devolve `null` nos três. */
export const percentuaisRealizacao = (ponto: PontoRealizacao): PercentuaisRealizacao => {
  const { n, realizados, cancelados, faltas } = ponto
  if (n <= 0 || realizados === null || cancelados === null || faltas === null) {
    return { realizados: null, cancelados: null, faltas: null }
  }
  return {
    realizados: (realizados * 100) / n,
    cancelados: (cancelados * 100) / n,
    faltas: (faltas * 100) / n,
  }
}

/**
 * Opções da ApexCharts para barras empilhadas de realizados, cancelados e faltas, em % dos
 * lançados. As porcentagens são calculadas aqui (não pelo `stackType: '100%'` da biblioteca),
 * para que ponto sem dado fique vazio. Sem import de runtime da biblioteca.
 */
export const montarOpcoesBarrasEmpilhadas = ({
  pontos,
  nomes,
  cores,
  formatarPercentual,
  formatarEixoY,
  formatarRotuloDia,
  formatarIntervalo,
  textoSemDados,
  altura,
  larguraGrafico,
}: EntradaOpcoesBarrasEmpilhadas): ApexOptions => {
  const categorias = pontos.map((ponto) => formatarRotuloDia(ponto.data))
  const percentuais = pontos.map(percentuaisRealizacao)

  let ultimoComDado = -1
  percentuais.forEach((p, i) => {
    if (p.realizados !== null) ultimoComDado = i
  })
  const passo = calcularPassoRotulosEixoX(pontos.length, larguraGrafico)
  const rotulosVisiveis = new Set(
    indicesRotulosEixoX(pontos.length, ultimoComDado, passo).map((i) => categorias[i])
  )

  // Coluna sem dado ganha um marcador na base: sem ele, ficaria igual à de 100% realizados.
  const marcadoresSemDado: NonNullable<NonNullable<ApexOptions['annotations']>['points']> =
    percentuais.flatMap((p, i) =>
      p.realizados === null
        ? [
            {
              x: categorias[i],
              y: 0,
              marker: {
                size: 4,
                shape: 'square',
                fillColor: cores.semDado,
                strokeColor: cores.semDado,
                strokeWidth: 0,
              },
            },
          ]
        : []
    )

  return {
    chart: {
      type: 'bar',
      stacked: true,
      height: altura ?? ALTURA_PADRAO,
      toolbar: { show: false },
      zoom: { enabled: false },
      fontFamily: 'inherit',
      animations: { enabled: false },
    },
    // Ordem do empilhamento: realizados na base, cancelados no meio, faltas no topo.
    series: [
      { name: nomes.realizados, data: percentuais.map((p) => p.realizados) },
      { name: nomes.cancelados, data: percentuais.map((p) => p.cancelados) },
      { name: nomes.faltas, data: percentuais.map((p) => p.faltas) },
    ],
    colors: [cores.realizados, cores.cancelados, cores.faltas],
    // Sem o 0,85 padrão da biblioteca: a cor declarada é a que tem o contraste medido.
    fill: { opacity: 1 },
    // O contorno na cor do cartão separa os segmentos: cada um só precisa contrastar com o fundo.
    stroke: { show: true, width: 2, colors: [cores.superficie] },
    plotOptions: { bar: { columnWidth: '70%', borderRadius: 0 } },
    dataLabels: { enabled: false },
    xaxis: {
      categories: categorias,
      labels: {
        rotate: 0,
        hideOverlappingLabels: true,
        formatter: (valor: string) => (rotulosVisiveis.has(String(valor)) ? String(valor) : ''),
        style: { colors: cores.texto, fontSize: '12px' },
      },
      axisTicks: { show: false },
      axisBorder: { show: false },
      tooltip: { enabled: false },
    },
    yaxis: {
      min: 0,
      max: 100,
      tickAmount: 4,
      labels: {
        formatter: (valor: number) => formatarEixoY(valor),
        style: { colors: cores.texto, fontSize: '12px' },
      },
    },
    grid: {
      borderColor: cores.grade,
      strokeDashArray: 4,
      xaxis: { lines: { show: false } },
      padding: { right: FOLGA_DIREITA },
    },
    tooltip: {
      shared: true,
      intersect: false,
      x: {
        formatter: (valor: number, opcoes?: { dataPointIndex?: number }) => {
          const ponto = pontos[opcoes?.dataPointIndex ?? -1]
          return ponto ? rotuloDoPonto(ponto, formatarRotuloDia, formatarIntervalo) : String(valor)
        },
      },
      y: { formatter: (valor: number) => formatarPercentual(valor) },
    },
    annotations: { points: marcadoresSemDado },
    legend: { show: false },
    noData: {
      text: textoSemDados,
      align: 'center',
      verticalAlign: 'middle',
      style: { color: cores.texto, fontSize: '14px' },
    },
  }
}
